import { HttpParams } from '@angular/common/http'
import { Injectable, inject } from '@angular/core'
import { ActivatedRoute, Params, Router } from '@angular/router'
import { FilterDescriptor, State } from '@progress/kendo-data-query'
import * as rison from 'rison-node'

import { DEFAULT_STATE } from './hunt-state-service'

export interface CyborgRouterSearchLink {
  routerLink: string | string[]
  queryParams: Params
}

export const GTE = 'gte'
export const LTE = 'lte'

@Injectable({ providedIn: 'root' })
export class HuntUrlService {
  private router = inject(Router)
  private activatedRoute = inject(ActivatedRoute)

  public stateToRouterLink(state: Partial<State>): CyborgRouterSearchLink {
    const queryParams = this.stateToUrlParams(state)
    return {
      routerLink: ['/hunt', 'hunts'],
      queryParams: queryParams,
    }
  }

  /**
   * Navigate to the search results for the given hunt state.
   * @param hunt search state
   */
  public gotoUrl(state: Partial<State>): void {
    const searchParams = this.stateToUrlParams(state)

    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: searchParams,
      queryParamsHandling: 'merge', // remove to replace all query params by provided
    })
  }

  /**
   * Transform a rison query parameter into a search state.
   * @param query active router query params
   * @returns search state
   */
  public searchStateFromString(query: Params): State {
    return {
      ...DEFAULT_STATE,
      ...rison.decode(query['state']),
    }
  }

  private stateToUrlParams(state: Partial<State>): Params {
    const targetState = { ...DEFAULT_STATE, ...state }

    const defaultKeys: string[] = Object.keys(DEFAULT_STATE)
    Object.keys(targetState).forEach((key) => {
      if (!defaultKeys.some((dkey) => dkey === key)) {
        delete targetState[key]
      }
    })

    return {
      state: rison.encode(targetState),
    }
  }

  public stateToHttpParams(filters: FilterDescriptor[]): HttpParams {
    let params = new HttpParams()

    if (filters)
      filters.forEach((value) => {
        const filteredValue = value as FilterDescriptor
        if (
          filteredValue.value &&
          Array.isArray(filteredValue.value) &&
          filteredValue.value.length
        ) {
          filteredValue.value.forEach((id) => {
            params = params.append(filteredValue.field as string, id)
          })
        } else {
          if ((filteredValue.operator as string) == GTE) {
            params = params.append(
              (filteredValue.field as string) + 'Start',
              new Date(filteredValue.value).toISOString(),
            )
          } else if ((filteredValue.operator as string) == LTE) {
            if (filteredValue.value instanceof Date) {
              const adjustedDate = new Date(filteredValue.value)
              adjustedDate.setDate(adjustedDate.getDate() + 1)
              params = params.append(
                (filteredValue.field as string) + 'End',
                adjustedDate.toISOString(),
              )
            } else
              params = params.append(
                (filteredValue.field as string) + 'End',
                new Date(filteredValue.value).toISOString(),
              )
          } else
            params = params.append(
              filteredValue.field as string,
              filteredValue.value,
            )
        }
      })
    return params
  }
}
